'use client';
import React, { useState, useEffect } from 'react';
import { forgeBehaviorService, CannabisAmount, CannabisTime, AlcoholLevel } from '@/lib/services/forgeBehaviorService';

const CANNABIS_AMOUNTS: { value: CannabisAmount; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: '1_2_puffs', label: '1–2 Puffs' },
  { value: '3_5_puffs', label: '3–5 Puffs' },
  { value: 'moderate_session', label: 'Moderate Session' },
  { value: 'heavy_session', label: 'Heavy Session' },
];

const CANNABIS_TIMES: { value: CannabisTime; label: string }[] = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
  { value: 'before_bed', label: 'Before Bed' },
];

const ALCOHOL_LEVELS: { value: AlcoholLevel; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'light', label: 'Light' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'heavy', label: 'Heavy' },
];

interface DailyBehaviorFormProps {
  onSaved?: () => void;
}

export default function DailyBehaviorForm({ onSaved }: DailyBehaviorFormProps) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    cannabisAmount: 'none' as CannabisAmount,
    cannabisTime: undefined as CannabisTime | undefined,
    cannabisNotes: '',
    alcoholLevel: 'none' as AlcoholLevel,
    hadEnergyDrink: false,
    energyDrinkCount: 0,
    coffeeCups: 0,
    hadLateNightEating: false,
    hadTakeaway: false,
    hadSocialEvent: false,
    wasHighStress: false,
    hadPoorSleepPrevious: false,
    caloriesBeforeCannabis: 0,
    caloriesAfterCannabis: 0,
    proteinBeforeCannabis: 0,
    proteinAfterCannabis: 0,
    unplannedCaloriesAfter: 0,
    sleepDurationHours: '' as string | number,
    sleepQualityRating: '' as string | number,
    morningEnergyRating: '' as string | number,
    trainingCompleted: false,
    trainingIntensity: '' as string | number,
    totalCalories: 0,
    totalProteinG: 0,
    totalSteps: 0,
    forgeScore: 0,
    notes: '',
  });

  useEffect(() => {
    forgeBehaviorService.getTodayLog().then(log => {
      if (log) {
        setForm({
          cannabisAmount: log.cannabisAmount,
          cannabisTime: log.cannabisTime,
          cannabisNotes: log.cannabisNotes || '',
          alcoholLevel: log.alcoholLevel,
          hadEnergyDrink: log.hadEnergyDrink,
          energyDrinkCount: log.energyDrinkCount,
          coffeeCups: log.coffeeCups,
          hadLateNightEating: log.hadLateNightEating,
          hadTakeaway: log.hadTakeaway,
          hadSocialEvent: log.hadSocialEvent,
          wasHighStress: log.wasHighStress,
          hadPoorSleepPrevious: log.hadPoorSleepPrevious,
          caloriesBeforeCannabis: log.caloriesBeforeCannabis,
          caloriesAfterCannabis: log.caloriesAfterCannabis,
          proteinBeforeCannabis: log.proteinBeforeCannabis,
          proteinAfterCannabis: log.proteinAfterCannabis,
          unplannedCaloriesAfter: log.unplannedCaloriesAfter,
          sleepDurationHours: log.sleepDurationHours ?? '',
          sleepQualityRating: log.sleepQualityRating ?? '',
          morningEnergyRating: log.morningEnergyRating ?? '',
          trainingCompleted: log.trainingCompleted,
          trainingIntensity: log.trainingIntensity ?? '',
          totalCalories: log.totalCalories,
          totalProteinG: log.totalProteinG,
          totalSteps: log.totalSteps,
          forgeScore: log.forgeScore,
          notes: log.notes || '',
        });
      }
      setLoading(false);
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const today = new Date().toISOString().split('T')[0];
    await forgeBehaviorService.saveBehaviorLog({
      logDate: today,
      cannabisAmount: form.cannabisAmount,
      cannabisTime: form.cannabisTime,
      cannabisNotes: form.cannabisNotes || undefined,
      alcoholLevel: form.alcoholLevel,
      hadEnergyDrink: form.hadEnergyDrink,
      energyDrinkCount: form.energyDrinkCount,
      coffeeCups: Number(form.coffeeCups) || 0,
      hadLateNightEating: form.hadLateNightEating,
      hadTakeaway: form.hadTakeaway,
      hadSocialEvent: form.hadSocialEvent,
      wasHighStress: form.wasHighStress,
      hadPoorSleepPrevious: form.hadPoorSleepPrevious,
      caloriesBeforeCannabis: Number(form.caloriesBeforeCannabis) || 0,
      caloriesAfterCannabis: Number(form.caloriesAfterCannabis) || 0,
      proteinBeforeCannabis: Number(form.proteinBeforeCannabis) || 0,
      proteinAfterCannabis: Number(form.proteinAfterCannabis) || 0,
      unplannedCaloriesAfter: Number(form.unplannedCaloriesAfter) || 0,
      sleepDurationHours: form.sleepDurationHours !== '' ? Number(form.sleepDurationHours) : undefined,
      sleepQualityRating: form.sleepQualityRating !== '' ? Number(form.sleepQualityRating) : undefined,
      morningEnergyRating: form.morningEnergyRating !== '' ? Number(form.morningEnergyRating) : undefined,
      trainingCompleted: form.trainingCompleted,
      trainingIntensity: form.trainingIntensity !== '' ? Number(form.trainingIntensity) : undefined,
      totalCalories: Number(form.totalCalories) || 0,
      totalProteinG: Number(form.totalProteinG) || 0,
      totalSteps: Number(form.totalSteps) || 0,
      forgeScore: Number(form.forgeScore) || 0,
      notes: form.notes || undefined,
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    onSaved?.();
  };

  const BoolToggle = ({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) => (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-all ${
        value
          ? 'bg-primary/15 border-primary/40 text-primary' :'bg-card-elevated border-border text-muted-foreground hover:border-primary/20 hover:text-foreground'
      }`}
    >
      <span className={`w-2 h-2 rounded-full ${value ? 'bg-primary' : 'bg-muted-foreground/40'}`} />
      {label}
    </button>
  );

  if (loading) {
    return (
      <div className="card-forge p-6 animate-pulse">
        <div className="h-6 bg-card-elevated rounded w-48 mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-10 bg-card-elevated rounded" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="card-forge p-4 sm:p-6 space-y-5 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-forge-heading text-lg font-bold tracking-wider">DAILY BEHAVIOR LOG</h2>
          <p className="text-muted-foreground text-xs mt-0.5">Optional · All fields are private · Data builds your intelligence report</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className={`w-full sm:w-auto px-4 py-2.5 sm:py-2 rounded-md text-sm font-bold tracking-wider transition-all ${
            saved
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' :'bg-primary/15 text-primary border border-primary/30 hover:bg-primary/25'
          }`}
        >
          {saving ? 'SAVING...' : saved ? '✓ SAVED' : 'SAVE LOG'}
        </button>
      </div>

      {/* Cannabis */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold tracking-widest text-muted-foreground uppercase">Cannabis Use</span>
          <div className="flex-1 h-px bg-border" />
        </div>
        <div className="flex flex-wrap gap-2">
          {CANNABIS_AMOUNTS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setForm(f => ({ ...f, cannabisAmount: opt.value }))}
              className={`flex-1 sm:flex-none min-w-[80px] px-3 py-2 rounded-md text-xs font-bold tracking-wider border transition-all ${
                form.cannabisAmount === opt.value
                  ? 'bg-primary/15 border-primary/40 text-primary' :'bg-card-elevated border-border text-muted-foreground hover:border-primary/20'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {form.cannabisAmount !== 'none' && (
          <div className="space-y-3 pl-3 border-l-2 border-primary/20">
            <div>
              <p className="text-xs text-muted-foreground mb-2 font-medium">Time Used</p>
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                {CANNABIS_TIMES.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, cannabisTime: opt.value }))}
                    className={`px-3 py-2 rounded-md text-xs font-bold tracking-wider border transition-all ${
                      form.cannabisTime === opt.value
                        ? 'bg-accent/15 border-accent/40 text-accent' :'bg-card-elevated border-border text-muted-foreground hover:border-accent/20'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Calories Before</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={form.caloriesBeforeCannabis || ''}
                  onChange={e => setForm(f => ({ ...f, caloriesBeforeCannabis: Number(e.target.value) }))}
                  className="w-full bg-card-elevated border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Calories After</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={form.caloriesAfterCannabis || ''}
                  onChange={e => setForm(f => ({ ...f, caloriesAfterCannabis: Number(e.target.value) }))}
                  className="w-full bg-card-elevated border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Protein Before (g)</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={form.proteinBeforeCannabis || ''}
                  onChange={e => setForm(f => ({ ...f, proteinBeforeCannabis: Number(e.target.value) }))}
                  className="w-full bg-card-elevated border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Unplanned Cals After</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={form.unplannedCaloriesAfter || ''}
                  onChange={e => setForm(f => ({ ...f, unplannedCaloriesAfter: Number(e.target.value) }))}
                  className="w-full bg-card-elevated border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
                  placeholder="0"
                />
              </div>
            </div>
            <input
              type="text"
              value={form.cannabisNotes}
              onChange={e => setForm(f => ({ ...f, cannabisNotes: e.target.value }))}
              className="w-full bg-card-elevated border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
              placeholder="Optional notes..."
            />
          </div>
        )}
      </div>

      {/* Alcohol */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold tracking-widest text-muted-foreground uppercase">Alcohol</span>
          <div className="flex-1 h-px bg-border" />
        </div>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
          {ALCOHOL_LEVELS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setForm(f => ({ ...f, alcoholLevel: opt.value }))}
              className={`px-3 py-2 rounded-md text-xs font-bold tracking-wider border transition-all ${
                form.alcoholLevel === opt.value
                  ? 'bg-amber-500/15 border-amber-500/40 text-amber-400' :'bg-card-elevated border-border text-muted-foreground hover:border-amber-500/20'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Other behaviors */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold tracking-widest text-muted-foreground uppercase">Other Behaviors</span>
          <div className="flex-1 h-px bg-border" />
        </div>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
          <BoolToggle label="Energy Drink" value={form.hadEnergyDrink} onChange={v => setForm(f => ({ ...f, hadEnergyDrink: v }))} />
          <BoolToggle label="Late Night Eating" value={form.hadLateNightEating} onChange={v => setForm(f => ({ ...f, hadLateNightEating: v }))} />
          <BoolToggle label="Takeaway" value={form.hadTakeaway} onChange={v => setForm(f => ({ ...f, hadTakeaway: v }))} />
          <BoolToggle label="Social Event" value={form.hadSocialEvent} onChange={v => setForm(f => ({ ...f, hadSocialEvent: v }))} />
          <BoolToggle label="High Stress Day" value={form.wasHighStress} onChange={v => setForm(f => ({ ...f, wasHighStress: v }))} />
          <BoolToggle label="Poor Sleep (Prev)" value={form.hadPoorSleepPrevious} onChange={v => setForm(f => ({ ...f, hadPoorSleepPrevious: v }))} />
        </div>
        {form.hadEnergyDrink && (
          <div className="flex items-center gap-3 pl-3">
            <label className="text-xs text-muted-foreground">Energy drinks:</label>
            <input
              type="number"
              inputMode="numeric"
              value={form.energyDrinkCount || ''}
              onChange={e => setForm(f => ({ ...f, energyDrinkCount: Number(e.target.value) }))}
              className="w-20 bg-card-elevated border border-border rounded-md px-2 py-2 text-sm text-foreground focus:outline-none focus:border-primary/50"
              min={0}
            />
          </div>
        )}
        <div className="flex items-center gap-3">
          <label className="text-xs text-muted-foreground">Coffee cups:</label>
          <input
            type="number"
            inputMode="numeric"
            value={form.coffeeCups || ''}
            onChange={e => setForm(f => ({ ...f, coffeeCups: Number(e.target.value) }))}
            className="w-20 bg-card-elevated border border-border rounded-md px-2 py-2 text-sm text-foreground focus:outline-none focus:border-primary/50"
            min={0}
          />
        </div>
      </div>

      {/* Sleep & Training */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold tracking-widest text-muted-foreground uppercase">Sleep & Training</span>
          <div className="flex-1 h-px bg-border" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Sleep Hours</label>
            <input
              type="number"
              inputMode="decimal"
              step="0.5"
              value={form.sleepDurationHours}
              onChange={e => setForm(f => ({ ...f, sleepDurationHours: e.target.value }))}
              className="w-full bg-card-elevated border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
              placeholder="7.5"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Sleep Quality (1–10)</label>
            <input
              type="number"
              inputMode="numeric"
              min={1} max={10}
              value={form.sleepQualityRating}
              onChange={e => setForm(f => ({ ...f, sleepQualityRating: e.target.value }))}
              className="w-full bg-card-elevated border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
              placeholder="8"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Morning Energy (1–10)</label>
            <input
              type="number"
              inputMode="numeric"
              min={1} max={10}
              value={form.morningEnergyRating}
              onChange={e => setForm(f => ({ ...f, morningEnergyRating: e.target.value }))}
              className="w-full bg-card-elevated border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
              placeholder="7"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <BoolToggle label="Training Completed" value={form.trainingCompleted} onChange={v => setForm(f => ({ ...f, trainingCompleted: v }))} />
          {form.trainingCompleted && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">Intensity (1–10):</label>
              <input
                type="number"
                inputMode="numeric"
                min={1} max={10}
                value={form.trainingIntensity}
                onChange={e => setForm(f => ({ ...f, trainingIntensity: e.target.value }))}
                className="w-16 bg-card-elevated border border-border rounded-md px-2 py-2 text-sm text-foreground focus:outline-none focus:border-primary/50"
              />
            </div>
          )}
        </div>
      </div>

      {/* Daily outcomes */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold tracking-widest text-muted-foreground uppercase">Daily Outcomes</span>
          <div className="flex-1 h-px bg-border" />
          <span className="text-2xs text-muted-foreground hidden sm:block">Links behavior to results</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Total Calories</label>
            <input
              type="number"
              inputMode="numeric"
              value={form.totalCalories || ''}
              onChange={e => setForm(f => ({ ...f, totalCalories: Number(e.target.value) }))}
              className="w-full bg-card-elevated border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
              placeholder="2000"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Protein (g)</label>
            <input
              type="number"
              inputMode="numeric"
              value={form.totalProteinG || ''}
              onChange={e => setForm(f => ({ ...f, totalProteinG: Number(e.target.value) }))}
              className="w-full bg-card-elevated border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
              placeholder="150"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Steps</label>
            <input
              type="number"
              inputMode="numeric"
              value={form.totalSteps || ''}
              onChange={e => setForm(f => ({ ...f, totalSteps: Number(e.target.value) }))}
              className="w-full bg-card-elevated border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
              placeholder="10000"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Forge Score</label>
            <input
              type="number"
              inputMode="numeric"
              value={form.forgeScore || ''}
              onChange={e => setForm(f => ({ ...f, forgeScore: Number(e.target.value) }))}
              className="w-full bg-card-elevated border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
              placeholder="85"
            />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Additional Notes</label>
        <textarea
          value={form.notes}
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          rows={2}
          className="w-full bg-card-elevated border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50 resize-none"
          placeholder="Any additional context for today..."
        />
      </div>

      <div className="pt-1 border-t border-border">
        <p className="text-2xs text-muted-foreground/60 italic">
          All entries are observations only. Correlation does not necessarily equal causation.
        </p>
      </div>
    </div>
  );
}
